import { createFileRoute, Link } from "@tanstack/react-router";

const TITLE = "34 Menopause Symptoms: Complete Checklist";
const DESCRIPTION =
  "The 34 symptoms of menopause and perimenopause, explained — with hormonal context and a free checklist for your doctor.";
const URL = "https://knowmeno.com/guide/34-symptoms";

export const Route = createFileRoute("/guide/34-symptoms")({
  head: () => ({
    meta: [
      { title: `${TITLE} — KnowMeno` },
      { name: "description", content: DESCRIPTION },
      { name: "robots", content: "index, follow, max-image-preview:large" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [
      { rel: "canonical", href: URL },
      { rel: "alternate", hrefLang: "en", href: URL },
      { rel: "alternate", hrefLang: "x-default", href: URL },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: TITLE,
          image: ["https://knowmeno.com/og-image.png?v=2"],
          datePublished: "2026-01-15",
          dateModified: "2026-06-29",
          inLanguage: "en",
          description: DESCRIPTION,
          author: { "@type": "Organization", name: "KnowMeno" },
          publisher: {
            "@type": "Organization",
            name: "KnowMeno",
            logo: { "@type": "ImageObject", url: "https://knowmeno.com/icon-512.png" },
          },
          mainEntityOfPage: URL,
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "Are there really 34 symptoms of menopause?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "The '34 symptoms' list is a widely-used educational framework, not an official medical diagnosis. It captures the most common physical, emotional and cognitive changes women report during perimenopause and menopause as estrogen and progesterone fluctuate and decline. Most women experience a cluster of them, not all 34.",
              },
            },
            {
              "@type": "Question",
              name: "Which of the 34 symptoms is most common?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Hot flashes, night sweats, sleep disruption, mood changes, brain fog, and irregular periods are reported by the majority of women in perimenopause. Joint pain, anxiety, and vaginal dryness are also extremely common but underreported.",
              },
            },
            {
              "@type": "Question",
              name: "How do I know if my symptoms are menopause or something else?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Pattern matters more than any one symptom. If you're over 40 and tracking 5+ symptoms from this list alongside cycle changes, perimenopause is the most likely explanation. Always rule out thyroid disease, anemia, and depression with a clinician — they mimic menopause closely.",
              },
            },
            {
              "@type": "Question",
              name: "Can I track these symptoms to show my doctor?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes — and you should. KnowMeno's Symptom Intelligence Report lets you log symptoms daily and generates a clinician-ready summary so your doctor sees the pattern, not just one snapshot.",
              },
            },
          ],
        }),
      },
    ],
  }),
  component: ThirtyFourSymptomsGuide,
});

const PLUM = "#4B1F4F";
const PINK = "#FF5C7A";
const CREAM = "#FAF7F5";
const BORDER = "#E8D5DA";
const TEXT = "#2D1A2E";
const MUTED = "#6E4B71";

interface SymptomGroup {
  title: string;
  hormonal: string;
  symptoms: { name: string; note: string }[];
}

const GROUPS: SymptomGroup[] = [
  {
    title: "Cycle & reproductive changes",
    hormonal:
      "Erratic estrogen and falling progesterone disrupt ovulation, so the menstrual cycle becomes unpredictable years before periods stop.",
    symptoms: [
      { name: "Irregular periods", note: "Cycles get shorter, longer, or skip altogether." },
      { name: "Heavy or flooding bleeds", note: "Unopposed estrogen thickens the uterine lining." },
      { name: "Loss of libido", note: "Lower estrogen and testosterone reduce desire and arousal." },
      { name: "Vaginal dryness", note: "Thinning tissues from estrogen decline; very treatable." },
      { name: "Painful sex (dyspareunia)", note: "Genitourinary syndrome of menopause (GSM)." },
      { name: "Urinary urgency or UTIs", note: "Estrogen also supports the bladder and urethra." },
    ],
  },
  {
    title: "Vasomotor symptoms",
    hormonal:
      "The brain's thermostat (hypothalamus) becomes hypersensitive to small temperature shifts as estrogen drops.",
    symptoms: [
      { name: "Hot flashes", note: "Sudden waves of heat, often with flushing and sweat." },
      { name: "Night sweats", note: "Hot flashes during sleep — a major driver of fatigue." },
      { name: "Cold flashes / chills", note: "The rebound after a hot flash; less talked about." },
    ],
  },
  {
    title: "Sleep & energy",
    hormonal:
      "Progesterone is calming and sleep-promoting; losing it — plus night sweats — fractures sleep architecture.",
    symptoms: [
      { name: "Insomnia", note: "Trouble falling or staying asleep; 3am wake-ups are classic." },
      { name: "Fatigue", note: "Cumulative from poor sleep + hormonal shifts." },
      { name: "Daytime sleepiness", note: "Often misread as 'low energy' or 'getting older'." },
    ],
  },
  {
    title: "Mood & mental health",
    hormonal:
      "Estrogen modulates serotonin and dopamine. Its swings — not just its decline — destabilize mood.",
    symptoms: [
      { name: "Mood swings", note: "Faster and more intense than premenstrual mood changes." },
      { name: "Anxiety", note: "New or worsened, often without an obvious trigger." },
      { name: "Depression", note: "Risk roughly doubles during perimenopause." },
      { name: "Irritability", note: "A short fuse, especially in the late luteal phase." },
      { name: "Panic attacks", note: "Sometimes mistaken for heart problems — always rule out." },
    ],
  },
  {
    title: "Cognition",
    hormonal:
      "Estrogen receptors are dense in the hippocampus and prefrontal cortex; fluctuations affect memory and processing speed.",
    symptoms: [
      { name: "Brain fog", note: "Slower recall, harder multitasking; usually improves post-menopause." },
      { name: "Memory lapses", note: "Word-finding, name recall, walking into rooms." },
      { name: "Difficulty concentrating", note: "Often worst around poor-sleep weeks." },
    ],
  },
  {
    title: "Body & musculoskeletal",
    hormonal:
      "Estrogen helps maintain bone, muscle, joint cartilage, and connective tissue.",
    symptoms: [
      { name: "Joint pain & stiffness", note: "Especially hands, knees, hips — often morning." },
      { name: "Muscle aches", note: "Recovery from exercise also slows." },
      { name: "Weight gain", note: "Especially around the abdomen; metabolic shift." },
      { name: "Bloating", note: "Slower gut motility plus water retention." },
      { name: "Breast tenderness", note: "From estrogen swings, not always cyclical now." },
      { name: "Headaches & migraines", note: "Can worsen mid-perimenopause, then improve." },
      { name: "Dizziness", note: "Often linked to hot flashes, blood pressure shifts, or sleep loss." },
      { name: "Electric-shock sensations", note: "Brief 'zaps' under the skin, often before a hot flash." },
      { name: "Tingling extremities", note: "Pins and needles in hands or feet." },
      { name: "Burning mouth or tongue", note: "Estrogen affects oral mucosa." },
      { name: "Gum problems", note: "Bleeding, sensitivity, recession." },
      { name: "Tinnitus", note: "Ringing or buzzing, sometimes intermittent." },
    ],
  },
  {
    title: "Skin, hair & nails",
    hormonal:
      "Estrogen supports collagen, skin hydration, and the hair growth cycle.",
    symptoms: [
      { name: "Dry, itchy skin", note: "Up to 30% collagen loss in the first 5 years." },
      { name: "Thinning hair", note: "Especially at the part line and temples." },
      { name: "Brittle nails", note: "Splitting, ridging, slower growth." },
      { name: "Body odor changes", note: "Sweat chemistry shifts during vasomotor events." },
    ],
  },
  {
    title: "Allergies & sensitivities",
    hormonal:
      "Estrogen modulates immune and inflammatory responses; its drop can unmask or amplify reactions.",
    symptoms: [
      { name: "New or worse allergies", note: "Seasonal, food, or skin reactions can intensify." },
      { name: "Heart palpitations", note: "Common and usually benign — always evaluate new onset." },
    ],
  },
];

const TOTAL = GROUPS.reduce((n, g) => n + g.symptoms.length, 0);

function ThirtyFourSymptomsGuide() {
  return (
    <div
      style={{
        background: CREAM,
        minHeight: "100vh",
        fontFamily: "system-ui, -apple-system, sans-serif",
        color: TEXT,
        padding: "32px 16px 80px",
      }}
    >
      <article
        style={{
          maxWidth: 760,
          margin: "0 auto",
          background: "#FFFFFF",
          borderRadius: 20,
          padding: "32px 24px",
          border: `0.5px solid ${BORDER}`,
        }}
      >
        <nav style={{ fontSize: 12, color: MUTED, marginBottom: 16 }}>
          <Link to="/" style={{ color: MUTED, textDecoration: "none" }}>
            KnowMeno
          </Link>{" "}
          / Guides / 34 Symptoms of Menopause
        </nav>

        <h1
          style={{
            fontSize: 32,
            lineHeight: 1.15,
            color: PLUM,
            margin: "0 0 12px",
            fontWeight: 500,
            letterSpacing: "-0.02em",
          }}
        >
          The 34 Symptoms of Menopause: A Complete Checklist
        </h1>
        <p style={{ fontSize: 16, lineHeight: 1.6, color: MUTED, marginTop: 0 }}>
          A practical, doctor-friendly list of the {TOTAL}+ most common symptoms women report in
          perimenopause and menopause — grouped by body system, with the hormonal reason behind
          each. If what you're feeling is on this list, you're not imagining it.
        </p>

        <section style={{ marginTop: 28 }}>
          <h2 style={{ fontSize: 22, color: PLUM, fontWeight: 500, marginBottom: 8 }}>
            Where the "34 symptoms" list comes from
          </h2>
          <p style={{ fontSize: 15, lineHeight: 1.7 }}>
            The list isn't an official diagnostic criterion. It's an educational framework that
            grew out of clinical observation — a way to legitimize the wide range of changes women
            describe when estrogen and progesterone become erratic and then decline. Most women
            experience a <strong>cluster of 8–15</strong>, not all 34. The value of the list is
            recognizing that one underlying hormonal shift can produce dozens of seemingly
            unrelated symptoms.
          </p>
        </section>

        {GROUPS.map((group) => (
          <section key={group.title} style={{ marginTop: 28 }}>
            <h2 style={{ fontSize: 20, color: PLUM, fontWeight: 500, marginBottom: 6 }}>
              {group.title}
            </h2>
            <p style={{ fontSize: 13, color: MUTED, lineHeight: 1.6, marginTop: 0, marginBottom: 14 }}>
              {group.hormonal}
            </p>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 8 }}>
              {group.symptoms.map((s) => (
                <li
                  key={s.name}
                  style={{
                    border: `1px solid ${BORDER}`,
                    background: CREAM,
                    borderRadius: 10,
                    padding: "10px 14px",
                    fontSize: 14,
                    lineHeight: 1.55,
                  }}
                >
                  <strong style={{ color: TEXT }}>{s.name}.</strong>{" "}
                  <span style={{ color: MUTED }}>{s.note}</span>
                </li>
              ))}
            </ul>
          </section>
        ))}

        <section
          style={{
            marginTop: 36,
            background: CREAM,
            border: `1px solid ${BORDER}`,
            borderRadius: 16,
            padding: "24px 20px",
          }}
        >
          <h2 style={{ fontSize: 22, color: PLUM, fontWeight: 500, marginTop: 0, marginBottom: 8 }}>
            Turn this checklist into a doctor-ready report
          </h2>
          <p style={{ fontSize: 15, color: TEXT, lineHeight: 1.65, marginTop: 0 }}>
            Recognising a symptom on a list is the easy part. What changes a 7-minute appointment
            is showing your clinician <strong>which symptoms cluster together, how often, and how
            they relate to your cycle and sleep</strong>. KnowMeno's Symptom Intelligence Report
            does that automatically — daily logs become a clean summary you can send before the
            visit.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 16 }}>
            <Link
              to="/sign-up"
              style={{
                background: PLUM,
                color: "#FFFFFF",
                textDecoration: "none",
                padding: "12px 18px",
                borderRadius: 12,
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              Start tracking free
            </Link>
            <Link
              to="/guide/perimenopause-test"
              style={{
                background: "#FFFFFF",
                color: PLUM,
                textDecoration: "none",
                padding: "12px 18px",
                borderRadius: 12,
                fontSize: 14,
                fontWeight: 500,
                border: `1px solid ${BORDER}`,
              }}
            >
              Take the self-assessment quiz
            </Link>
          </div>
        </section>

        <section style={{ marginTop: 32 }}>
          <h2 style={{ fontSize: 22, color: PLUM, fontWeight: 500, marginBottom: 8 }}>
            Related reading
          </h2>
          <ul style={{ fontSize: 15, lineHeight: 1.9, paddingLeft: 20 }}>
            <li>
              <Link to="/menopause-guide" style={{ color: PINK }}>
                The complete menopause guide
              </Link>
            </li>
            <li>
              <Link to="/guide/fsh-levels" style={{ color: PINK }}>
                Understanding FSH levels in perimenopause
              </Link>
            </li>
            <li>
              <Link to="/guide/heart-palpitations" style={{ color: PINK }}>
                Heart palpitations & menopause
              </Link>
            </li>
            <li>
              <Link to="/guide/menopause-age-calculator" style={{ color: PINK }}>
                Menopause age calculator
              </Link>
            </li>
          </ul>
        </section>

        <p
          style={{
            marginTop: 36,
            fontSize: 12,
            color: MUTED,
            lineHeight: 1.55,
            borderTop: `1px solid ${BORDER}`,
            paddingTop: 16,
          }}
        >
          Educational content only. KnowMeno does not provide medical advice, diagnosis, or
          treatment. Talk to a qualified clinician about your symptoms, particularly any new chest
          pain, severe bleeding, or mental-health concerns.
        </p>
      </article>
    </div>
  );
}
